<!--
  ZoneTable part — the <thead>: the master checkbox, the column labels, their tooltips and the sort
  controls. INTERNAL: not a Zone component, no story, not in llms.txt. Rendered only by Table.vue.

  Presentational. The class rules it renders with come from `./cells`, shared with Body.vue so a
  <th> and the <td> under it can never disagree about width, alignment or sticky layer.

  It forwards the `#header-KEY` slots it is given straight through, which is why Table.vue passes
  `$slots` down: a dynamic slot name cannot be re-declared, only relayed.
-->

<template>
  <thead :class="theadClasses">
    <!--
      `bg-gray-50` on the ROW, not just on each <th>. The selection cell inherits its
      background (it has to, so a body row's hover/expanded tint reaches it), and an
      unpainted <tr> made `background-color: inherit` resolve to TRANSPARENT — which is why
      columns scrolling past were visible straight through the master-checkbox cell.
    -->
    <tr class="bg-gray-50">
      <th
        v-if="selectable"
        scope="col"
        :class="selectionClasses"
      >
        <ZoneCheckbox
          data-testid="select-all"
          :model-value="allSelected"
          :indeterminate="someSelected"
          :disabled="selectAllDisabled"
          @update:model-value="$emit('toggle-all', $event)"
        />
      </th>
      <th
        v-for="(column, columnIndex) in columns"
        :key="column.key"
        scope="col"
        :aria-sort="ariaSortFor(column)"
        :class="cellClasses(column, columnIndex)"
      >
        <div class="flex min-w-0 items-center gap-1">
          <!--
            The label deliberately does NOT take `flex-1`: the Zone guideline is "icons
            should always be next to text", with sorting alone pushed to the far right by
            the sort button's own `ml-auto`. `min-w-0` still lets a long label truncate.
          -->
          <slot
            :name="`header-${column.key}`"
            :column="column"
            :sort="sort"
          >
            <span class="min-w-0 truncate">{{ column.label }}</span>
          </slot>

          <ZoneIcon
            v-if="column.tooltip"
            icon="info"
            :tooltip="column.tooltip"
            class="shrink-0 text-gray-600"
          />

          <!--
            `label` is optional on TableColumn but `key` never is, so fall back to the key rather
            than interpolate "undefined" into the button's accessible name. `||` and not `??`:
            a deliberately blank header (`label: ''`, legitimate for an icon-only column — the
            visible span above renders it empty) leaves a screen reader with "Sort by " otherwise.
          -->
          <button
            v-if="column.sortable"
            type="button"
            :class="sortButtonClasses(column)"
            :aria-label="t('sort_by', { column: column.label || column.key })"
            :data-testid="`sort-${column.key}`"
            @click="$emit('toggle-sort', column)"
          >
            <ZoneIcon :icon="sortIconFor(column)" />
          </button>
        </div>
      </th>
    </tr>
  </thead>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import ZoneIcon, { type IconName } from '../Icon.vue';
import ZoneCheckbox from '../Checkbox.vue';
import { headerCellClasses, selectionCellClasses, sortButtonClasses as sortButtonClassesFor, type StickyOptions } from './cells';
import type { TableColumn, TableSort } from './types';

defineOptions({ name: 'ZoneTableHead' });

const { t } = useI18n();

const props = defineProps({
  columns: {
    type: Array as PropType<TableColumn[]>,
    required: true,
  },
  sort: {
    type: Object as PropType<TableSort | null>,
    default: null,
  },
  selectable: {
    type: Boolean,
    required: true,
  },
  /** Tri-state master checkbox: checked / indeterminate / neither. */
  allSelected: {
    type: Boolean,
    required: true,
  },
  someSelected: {
    type: Boolean,
    required: true,
  },
  selectAllDisabled: {
    type: Boolean,
    required: true,
  },
  stickyHeader: {
    type: Boolean,
    required: true,
  },
  stickyOptions: {
    type: Object as PropType<StickyOptions>,
    required: true,
  },
});

defineEmits(['toggle-all', 'toggle-sort']);

// z-30, not z-20: the body's pinned checkbox cell is itself z-20, and a sticky header that
// ties with it loses on DOM order — the body would scroll over the header.
const theadClasses = computed<string>(() => (props.stickyHeader ? 'sticky top-0 z-30' : ''));

const selectionClasses = computed<string>(() => selectionCellClasses(props.stickyOptions, 'header'));

function cellClasses(column: TableColumn, columnIndex: number): string {
  return headerCellClasses(column, columnIndex, props.stickyOptions);
}

function sortButtonClasses(column: TableColumn): string {
  return sortButtonClassesFor(props.sort?.key === column.key);
}

function sortIconFor(column: TableColumn): IconName {
  if (props.sort?.key !== column.key) {
    return 'swap_vert' as IconName;
  }

  return (props.sort.direction === 'asc' ? 'arrow_upward' : 'arrow_downward') as IconName;
}

function ariaSortFor(column: TableColumn): string | undefined {
  if (!column.sortable) {
    return undefined;
  }
  if (props.sort?.key !== column.key) {
    return 'none';
  }

  return props.sort.direction === 'asc' ? 'ascending' : 'descending';
}
</script>

<i18n lang="json">
{
  "en": {
    "sort_by": "Sort by {column}"
  }
}
</i18n>
